import styled from '@emotion/styled'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { STEP_LAYOUT_HEIGHT } from 'modules/flow/components/shared/shared-step-layout.styled'
import { DEFAULT_FONT_STYLES, DEFAULT_THEME_COLORS, getTitleFonts } from 'css/theme'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import useConfig from 'modules/config/state/use-config'
import { SharingConfig } from 'modules/config/state/config-types'
import CopyToClipboardField from './components/copy-to-clipboard-field'
import { useTranslation, Trans } from 'next-i18next'

interface SocialMediaConfig {
  name: string
  link: string
  icon: string
}

const SocialPanel = () => {
  const analytics = getAnalyticsInstance() // Initialize analytics
  const config = useConfig()
  const { t } = useTranslation('common')

  if (!config?.sharing) {
    return null // Return null if there's no sharing data
  }

  const socialMediaConfigs = makeSocialMediaConfigs(config?.sharing, config?.campaignName, '', t)
  const directLink = config?.sharing?.directLink

  return (
    <SocialPanelWrapper data-testid="social-panel">
      <SocialPanelTitle>
        {t('socialPanel.title', 'Increase your impact by sharing')}
      </SocialPanelTitle>
      <SocialPanelSubtitle>
        <Trans i18nKey="socialPanel.subtitle">
          Sharing helps raise awareness for the cause. 55% of people who engage with nonprofits on
          social media end up taking some sort of action. With your help we can make a bigger impact
          around the world.
        </Trans>
      </SocialPanelSubtitle>
      <SocialMediaList>
        {socialMediaConfigs.map((c) => (
          <SocialMediaListItem
            key={c.name}
            data-id={`social-panel-${c.name.toLowerCase()}-button`}
            onClick={() => {
              // Track share attempt inline
              analytics.track('share', {
                platform: c.name,
              })

              window.open(c.link, '_blank', 'noopener,noreferrer')
            }}
          >
            <SocialMediaListItemIcon name={c.icon} w={40} h={40} />
            <SocialMediaListItemText>{c.name}</SocialMediaListItemText>
          </SocialMediaListItem>
        ))}
      </SocialMediaList>

      {directLink && (
        <CopyToClipboardField
          label={t('socialPanel.copyLink.label', 'Copy Link')}
          value={directLink}
        />
      )}
    </SocialPanelWrapper>
  )
}

export default SocialPanel

const SocialPanelWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px;
  gap: 16px;
  display: flex;
  flex-direction: column;
  min-height: ${STEP_LAYOUT_HEIGHT};

  border-top: 1px #d7dbdf solid;

  @media (min-width: 784px) {
    border-top: none;
    border-left: 1px #d7dbdf solid;
  }
`

const SocialPanelTitle = styled.div`
  ${getTitleFonts()};
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-20']};
`

const SocialPanelSubtitle = styled.div`
  padding: 16px;
  border-radius: 16px;
  background: #425ccd1f;
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-20']};
  font-size: ${DEFAULT_FONT_STYLES['--theme-font--caption__font-size']};
  font-weight: ${DEFAULT_FONT_STYLES['--theme-font--caption__font-weight']};
  line-height: ${DEFAULT_FONT_STYLES['--theme-font--caption__line-height']};
`

const SocialMediaList = styled.div`
  gap: 16px;
  padding: 0 16px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
`

const SocialMediaListItem = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  flex-direction: column;
  cursor: pointer;
`

const SocialMediaListItemIcon = styled(UiIcon)``

const SocialMediaListItemText = styled.div`
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-20']};
  font-size: ${DEFAULT_FONT_STYLES['--theme-font--caption__font-size']};
  font-weight: ${DEFAULT_FONT_STYLES['--theme-font--caption__font-weight']};
  line-height: ${DEFAULT_FONT_STYLES['--theme-font--caption__line-height']};
`

const makeSocialMediaConfigs = (
  sharing: SharingConfig,
  campaignName?: string,
  shareableLink = '',
  t?: (key: string, defaultValue: string) => string,
): SocialMediaConfig[] => {
  const configs: SocialMediaConfig[] = []

  configs.push({
    name: t ? t('socialPanel.smsName', 'SMS') : 'SMS',
    link: `sms:?body=${encodeURIComponent(sharing.smsText || '')}`,
    icon: 'social_text',
  })

  configs.push({
    name: t ? t('socialPanel.emailName', 'Email') : 'Email',
    link: `mailto:?subject=${encodeURIComponent(
      sharing.emailSubject || '',
    )}&body=${encodeURIComponent(sharing.emailBody || '')}`,
    icon: 'social_email',
  })

  configs.push({
    name: t ? t('socialPanel.facebookName', 'Facebook') : 'Facebook',
    link: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      sharing?.facebookText || '',
    )}`,
    icon: 'social_facebook',
  })

  configs.push({
    name: t ? t('socialPanel.xName', 'X') : 'X',
    link: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      shareableLink,
    )}&text=${encodeURIComponent(sharing.twitterText || '')}`,
    icon: 'social_x',
  })

  return configs
}
